// React
import React, { useState, useRef, useEffect, useLayoutEffect } from "react";

// Libraries
import PropTypes from "prop-types";
import { Modal, Icon, Image } from "semantic-ui-react";
import EmojiPicker from "emoji-picker-react";
import Lightbox from "yet-another-react-lightbox";
import { Fullscreen, Zoom, Download } from "yet-another-react-lightbox/plugins";
import TextAreaAutoSize from "react-textarea-autosize";
import { isEmpty } from "lodash";

// Utils
import { getObjectURL } from "@Utils";
import { autoCapitalize } from "@Utils/functionality";

// Styles
import styles from "./styles.module.css";

const NotePreview = ({ text = "", handleDismiss, handleSend, blackListKeyWords }) => {
	const [caption, setCaption] = useState(text || "");
	const [showEmojis, setShowEmojis] = useState(false);
	const [lightbox, setLightbox] = useState({ open: false });
	const [height, setHeight] = useState(64);
	const [file, setFile] = useState(null);
	const [fileUri, setFileUri] = useState(null);
	const textAreaRef = useRef(null);
	const highlightsRef = useRef(null);
	const addFileInputRef = useRef(null);

	useLayoutEffect(() => {
		if (textAreaRef.current) {
			const newHeight = textAreaRef.current.offsetHeight + 16;
			if (newHeight >= 210) {
				setHeight(210);
			} else if (newHeight >= 64) {
				setHeight(newHeight);
			} else {
				setHeight(64);
			}
		}
	}, [caption]);

	useEffect(() => {
		let cancelled = false;
		(async () => {
			if (!file) {
				setFileUri(null);
				return;
			}
			const uri = await getObjectURL(file);
			if (!cancelled) {
				setFileUri(uri);
			}
		})();

		return () => {
			cancelled = true;
		};
	}, [file]);

	useEffect(() => {
		return () => {
			if (fileUri) {
				URL.revokeObjectURL(fileUri);
			}
		};
	}, [fileUri]);

	const toggleEmojiPicker = () => {
		setShowEmojis((prev) => {
			return !prev;
		});
	};

	const handleEmojiInput = ({ emoji }) => {
		setCaption((prev) => {
			return prev + emoji;
		});
		setShowEmojis(false);
	};

	const handleInput = (e) => {
		let { value } = e.target;
		value = autoCapitalize(value);
		setCaption(value);
	};

	const handleScroll = (e) => {
		if (highlightsRef.current) {
			highlightsRef.current.scrollTop = e.target.scrollTop;
		}
	};

	const renderHighlightedText = () => {
		if (!blackListKeyWords || blackListKeyWords.length === 0) return caption;
		const escapedKeywords = blackListKeyWords.map((k) => {
			return k.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
		});
		const regex = new RegExp(`\\b(${escapedKeywords.join("|")})\\b`, "gi");
		const parts = caption.split(regex);
		return parts.map((part, i) => {
			if (
				blackListKeyWords.some((k) => {
					return new RegExp(`^${k}$`, "i").test(part);
				})
			) {
				return (
					<span key={i} className={styles.blockedMark}>
						{part}
					</span>
				);
			}
			return <span key={i}>{part}</span>;
		});
	};

	const handleSendMessage = async () => {
		if (isEmpty(caption.trim()) && !file) return;
		const result = await handleSend({ file, caption: caption.trim() });
		if (result !== false) {
			handleDismiss();
		}
	};

	const handleOpenLightbox = () => {
		if (fileUri) {
			setLightbox({ open: true });
		}
	};

	const handleCloseLightbox = () => {
		setLightbox({ open: false });
	};

	const handleAddFile = (e) => {
		const selected = e.target.files?.[0];
		if (selected) {
			setFile(selected);
		}
		e.target.value = null;
	};

	const handleOpenFileInput = () => {
		if (addFileInputRef.current) {
			addFileInputRef.current.click();
		}
	};

	const handleRemoveFile = () => {
		setFile(null);
		setFileUri(null);
	};

	const handleKeyDown = async (e) => {
		if (e.key === "Enter" && !e.shiftKey) {
			e.preventDefault();
			await handleSendMessage();
		}
	};

	return (
		<Modal className={styles.modal} open closeIcon closeOnDimmerClick={false} onClose={handleDismiss}>
			<Modal.Header className={styles.header}>Guardar Nota</Modal.Header>
			<Modal.Content className={styles.content}>
				<div className={styles.fileContainer}>
					{fileUri ? (
						<div className={styles.imageContainer}>
							<Lightbox
								open={lightbox.open}
								plugins={[Download, Fullscreen, Zoom]}
								close={handleCloseLightbox}
								slides={[{ src: fileUri }]}
								zoom={{
									maxZoomPixelRatio: 5,
									zoomInMultiplier: 2,
								}}
							/>
							<Image className={styles.image} src={fileUri} onClick={handleOpenLightbox} />
							<div
								className={styles.removeButton}
								onClick={handleRemoveFile}
								role="button"
								tabIndex={0}
								onKeyDown={(e) => {
									return e.key === "Enter" && handleRemoveFile();
								}}
								aria-label="Quitar imagen">
								<Icon name="close" className={styles.removeIcon} />
							</div>
						</div>
					) : (
						<div
							className={styles.addBox}
							onClick={handleOpenFileInput}
							role="button"
							tabIndex={0}
							onKeyDown={(e) => {
								return e.key === "Enter" && handleOpenFileInput();
							}}
							aria-label="Agregar imagen">
							<Icon name="plus" size="huge" className={styles.addIcon} />
							<span className={styles.addLabel}>Agregar imagen (opcional)</span>
						</div>
					)}
					<input ref={addFileInputRef} type="file" accept="image/*" hidden onChange={handleAddFile} />
				</div>
				<div className={styles.inputContainer} style={{ height }}>
					{showEmojis && (
						<div className={styles.picker}>
							<EmojiPicker
								onEmojiClick={handleEmojiInput}
								autoFocusSearch={false}
								previewConfig={{ showPreview: false }}
								style={{ zIndex: 5 }}
							/>
						</div>
					)}
					<div
						className={showEmojis ? styles.focusedIconContainer : styles.buttonIconContainer}
						role="button"
						tabIndex="-1"
						onClick={toggleEmojiPicker}>
						<Icon name={showEmojis ? "close" : "smile outline"} size="large" />
					</div>

					<div className={styles.input}>
						<div className={styles.inputWrapper}>
							<div ref={highlightsRef} className={styles.highlights}>
								{renderHighlightedText()}
								{caption.endsWith("\n") && <br />}
							</div>
							<TextAreaAutoSize
								placeholder="Escriba una nota"
								value={caption}
								onChange={handleInput}
								onKeyDown={handleKeyDown}
								id="note-textarea"
								name="textarea"
								ref={textAreaRef}
								className={styles.textarea}
								minRows={1}
								maxRows={10}
								onScroll={handleScroll}
							/>
						</div>
					</div>
					<div className={styles.buttonIconContainer} role="button" tabIndex="-1" onClick={handleSendMessage}>
						<Icon name="send" size="large" />
					</div>
				</div>
			</Modal.Content>
		</Modal>
	);
};

NotePreview.propTypes = {
	text: PropTypes.string,
	handleDismiss: PropTypes.func,
	handleSend: PropTypes.func,
	blackListKeyWords: PropTypes.array,
};

export default NotePreview;
